import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro'
import { useQuery } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import type { PillProps } from 'ext-ui/components/Pill/Pill'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import { spacing } from 'ext-ui/style/spacing'
import React, { useLayoutEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import VendorFlyout from 'src/component-flyout/VendorFlyout/VendorFlyout'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import DataGrid, {
  DATA_GRID_HEADER_HEIGHT,
  DATA_GRID_HEIGHT_OFFSET,
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useDebounceValue } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { vendorQueries } from 'src/queries/vendors/vendors.queries'
import { Vendor, VendorStatus } from 'src/queries/vendors/vendors.types'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { canManageBillPay } from 'src/utils/billPayAccess'
import { isBillPayEnabled } from 'src/utils/featureFlags'

import { ChevronIcon } from './ChevronIcon'
import { PlusIcon } from './PlusIcon'
import { VendorsEmptyIllustration } from './VendorsEmptyIllustration'
import style from './VendorsPage.style'

type Translate = ReturnType<typeof useTranslate>

/** Placeholder for the optional columns the backend has not filled in. */
const EM_DASH = '—'

/** Backend page size — the server owns filtering and pagination. */
const PAGE_SIZE = 12

/**
 * `DataGrid` takes a numeric height and subtracts its shared height offset — it
 * cannot take `height: 100%`. So filling the leftover vertical space means
 * measuring it and adding that offset back. A fixed height here instead clipped
 * rows unreachably: both `LayoutPage fixedHeight` and `.vendors-box` set
 * `overflow: hidden`, so nothing could scroll to reach them.
 */
const MIN_VISIBLE_TABLE_ROWS = 3

/**
 * Floor for the measured height, so a very short viewport degrades to a small
 * scrollable table rather than a header with no rows. Also the first-paint
 * value, before the observer has measured.
 */
const MIN_TABLE_HEIGHT =
  DATA_GRID_HEADER_HEIGHT +
  MIN_VISIBLE_TABLE_ROWS * DataGridRowHeight.Default +
  DATA_GRID_HEIGHT_OFFSET

/**
 * Vendor-level status label + pill color. The network mapper derives ARCHIVED
 * from archivedAt before considering the server-authoritative payment method.
 */
function statusPresentation(
  status: VendorStatus,
  translate: Translate,
): { label: string; color?: PillProps['color'] } {
  switch (status) {
    case VendorStatus.Active:
      return { label: translate('Active'), color: 'highlight' }
    case VendorStatus.Archived:
      return { label: translate('Archived') }
    case VendorStatus.ActionRequired:
      return { label: translate('Action Required'), color: 'severe' }
  }
}

/** Every text column renders through `DataGridCell` (shared truncation +
 * disabled/hover treatment), so the columns below only supply the value. */
function renderTextCell(
  text: (vendor: Vendor) => string,
): (params: GridCellParams<Vendor>) => React.ReactElement {
  return (params) => <DataGridCell params={params} text={text(params.row)} />
}

/**
 * Bill Pay M2 vendor management (design source: extend-design-render
 * `invoice-pay-prototype` app/vendors/page.tsx, "MVP Milestone 2" version).
 *
 * Column set per the prototype's MVP-M2 gating: Vendor (lead) · Vendor
 * Contact · Remittance Email · Vendor Description · Status. "Open Balance"
 * and "Payment Method" are deliberately absent for Milestone 2
 * (`showOpenBalance`/`showPaymentMethod` are both false there); "Source" is
 * Andrew's-prototype-only.
 *
 * Gating mirrors the Bank Account settings surface (BankAccountsPage.tsx)
 * in shape — launch gate + `VIEW_PAYABLES` tier + the shared
 * `canManageBillPay` role check (owner/admin + full-visibility bookkeepers,
 * EX-40473). Launch gate = the whole-feature Bill Pay gate (`bill_pay` OR the
 * `bill_pay_allowlist` beta list) — the same gate every Bill Pay surface
 * reads, so this page and the nav can never disagree.
 */
const VendorsPage: React.FC = () => {
  const translate = useTranslate()
  const currentUser = useCurrentUser()
  const appName = useSelector(selectAppName)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)

  const billPayVendorsEnabled = isBillPayEnabled(remoteConfig, organizationId)
  const vendorEditEnabled = isBillPayEnabled(remoteConfig, organizationId)
  const vendorArchiveEnabled = isBillPayEnabled(remoteConfig, organizationId)
  const { data: userFeatures, isLoading: userFeaturesLoading } =
    useQueryUserFeatures(
      [UserFeature.VIEW_PAYABLES, UserFeature.ORGANIZATION_USER_STATISTICS],
      {
        enabled: billPayVendorsEnabled,
      },
    )

  const canViewPayables = userFeatures?.[UserFeature.VIEW_PAYABLES] ?? false
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so this
  // gate and the nav item can never diverge — see src/utils/billPayAccess.
  const hasAccess =
    billPayVendorsEnabled &&
    canManageBillPay({
      hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
      organizationRole: currentUser?.organizationRole,
      orgUserStatisticsEnabled:
        userFeatures?.[UserFeature.ORGANIZATION_USER_STATISTICS],
    }) &&
    canViewPayables

  // Gate the vendors query on access AND a resolved org, so a deep link with
  // the flag off stays silent instead of firing a dark request (same
  // convention as BankAccountsPage / EX-40413 review fix).
  // Open-state for the Add/Edit-Vendor flyout: `{ mode: 'add' }` (header CTA),
  // `{ mode: 'resume', vendorId }` (EX-40554), `{ mode: 'edit', vendorId }`
  // (EX-40771), or `null` (closed). A discriminated union rather than
  // `'add' | string` so the compiler (not a vendor id happening to collide with
  // the literal `'add'`) is what keeps the modes apart.
  //
  // EX-40554: resume a vendor stuck in Action Required — clicking its row
  // reopens the flyout with `resumeVendorId` set, which jumps straight to the
  // bank-account/acknowledgement step for that vendor.
  //
  // EX-40771 / EX-40761: every other vendor row opens the same flyout when
  // editing or archiving is enabled — both now ride the single Bill Pay gate
  // (EX-40841), so they are always on or off together; the edit-off/
  // archive-only read-only mode below is dead code from when they were
  // independent, kept only because collapsing it is a separate cleanup.
  // Action Required keeps its dedicated resume path so the payment-setup
  // flow does not regress.
  type FlyoutTarget =
    | { mode: 'add' }
    | { mode: 'resume'; vendorId: string }
    | { mode: 'edit'; vendorId: string }
  const [flyoutTarget, setFlyoutTarget] = useState<FlyoutTarget | null>(null)

  // Action Required -> resume (unchanged, and NOT gated: resuming ACH setup
  // is Vendors-page behavior). Every other status -> the edit-mode shell
  // when the Bill Pay gate is on. With it off, those rows remain inert.
  const flyoutTargetForVendor = (vendor: Vendor): FlyoutTarget | null => {
    if (vendor.status === VendorStatus.ActionRequired) {
      return { mode: 'resume', vendorId: vendor.id }
    }
    return vendorEditEnabled || vendorArchiveEnabled
      ? { mode: 'edit', vendorId: vendor.id }
      : null
  }

  /** A row is actionable if clicking it would open something. Drives the row
   *  cursor and whether the row-end caret renders at all. */
  const isVendorRowActionable = (vendor: Vendor): boolean =>
    vendor.status === VendorStatus.ActionRequired ||
    vendorEditEnabled ||
    vendorArchiveEnabled

  // Measured table height (see MIN_TABLE_HEIGHT above). A callback ref rather
  // than a `useRef`, because the table only mounts on the populated branch —
  // well after the early returns below — so the effect has to re-run when the
  // element actually appears, which a ref object's `.current` mutation would
  // not trigger.
  const [tableViewport, setTableViewport] = useState<HTMLDivElement | null>(
    null,
  )
  const [tableHeight, setTableHeight] = useState(MIN_TABLE_HEIGHT)

  // useLayoutEffect because this is pre-paint DOM measurement, not network
  // work (src/CLAUDE.md).
  //
  // The first measure is synchronous, on purpose. ResizeObserver does fire on
  // observe(), but its callbacks are delivered AFTER requestAnimationFrame in
  // the rendering step — so routing that first one through the rAF coalescer
  // below would paint a floor-height table and expand it a frame later, which
  // is the flicker useLayoutEffect exists to avoid. Subsequent callbacks are
  // coalesced, because a window-resize drag otherwise pushes a new page-level
  // height — and a fresh generated style rule inside DataGrid — every frame.
  useLayoutEffect(() => {
    if (!tableViewport) return undefined
    const measure = (): void => {
      setTableHeight(
        Math.max(
          MIN_TABLE_HEIGHT,
          tableViewport.clientHeight + DATA_GRID_HEIGHT_OFFSET,
        ),
      )
    }
    measure()

    let frame: number | null = null
    const observer = new ResizeObserver(() => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = null
        measure()
      })
    })
    observer.observe(tableViewport)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [tableViewport])

  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const debouncedSearch = useDebounceValue(searchQuery.trim(), 300)
  const listParams = useMemo(
    () => ({
      page,
      count: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  )
  const vendorsReady = hasAccess && Boolean(organizationId)
  const vendorsQuery = useQuery({
    ...vendorQueries.list(organizationId, listParams),
    enabled: vendorsReady,
  })

  if (
    !loadedRemoteConfig ||
    currentUser === undefined ||
    (billPayVendorsEnabled && userFeaturesLoading)
  ) {
    return <LoadingBackgroundPage />
  }

  if (!hasAccess) {
    return <NotFoundBackgroundPage />
  }

  if (vendorsQuery.isLoading || !organizationId) {
    return <LoadingBackgroundPage />
  }

  const vendorsData = vendorsQuery.data
  const vendors = vendorsData?.vendors ?? []
  const pagination = vendorsData?.pagination ?? {
    page,
    pageItemCount: 0,
    totalItems: 0,
    numberOfPages: 0,
  }
  const rangeStart =
    pagination.totalItems === 0 ? 0 : pagination.page * PAGE_SIZE + 1
  const rangeEnd =
    pagination.totalItems === 0
      ? 0
      : pagination.page * PAGE_SIZE + pagination.pageItemCount
  const isFirstPage = pagination.page === 0
  const isLastPage =
    pagination.numberOfPages === 0 ||
    pagination.page >= pagination.numberOfPages - 1
  // Keyed off the server's own total, not this page's row count. With
  // keepPreviousData the pager stays live while stale data shows, so paging
  // past the last page returns zero rows — and a row-count check would swap the
  // toolbar, table AND pagination footer for "No vendors yet", stranding an org
  // that has vendors with no control to get back.
  const showPageEmptyState =
    pagination.totalItems === 0 && searchQuery.trim() === ''

  // No column is sortable. The shared DataGrid hard-codes
  // `sortingMode="server"`, so MUI never sorts locally and a header click does
  // nothing without an `onSortModelChange` handler — MUI's default
  // `sortable: true` was rendering a sort arrow that promised behavior the
  // page does not have. Sorting is also not in the design (the prototype's
  // table has no sort affordance; it sorts by name and pins a just-added
  // vendor), and the API only accepts NAME / CREATED_AT / UPDATED_AT, so four
  // of these five columns could not be sorted server-side even if it were.
  const columns: GridColDef<Vendor>[] = [
    {
      field: 'name',
      headerName: translate('Vendor'),
      flex: 2,
      sortable: false,
      renderCell: renderTextCell((vendor) => vendor.name),
    },
    {
      field: 'contactName',
      headerName: translate('Vendor Contact'),
      flex: 1.5,
      sortable: false,
      renderCell: renderTextCell((vendor) => vendor.contactName ?? EM_DASH),
    },
    {
      field: 'email',
      headerName: translate('Remittance Email'),
      flex: 2,
      sortable: false,
      renderCell: renderTextCell((vendor) => vendor.email),
    },
    {
      field: 'notes',
      headerName: translate('Vendor Description'),
      flex: 2,
      sortable: false,
      renderCell: renderTextCell((vendor) => vendor.notes ?? EM_DASH),
    },
    {
      field: 'status',
      headerName: translate('Status'),
      flex: 1,
      sortable: false,
      // A Pill is a fixed-size box, so a narrow column clips it outright
      // rather than truncating with an ellipsis the way the text cells do.
      // Sized for the longest label ("Action Required" / fr-CA "Action
      // requise") plus the cell's padding — matches the CardFeeds status
      // column, which uses the same PillCell at 130 for the longer
      // "Connection Failed" / "Échec de la connexion".
      minWidth: 130,
      renderCell: (params: GridCellParams<Vendor>): React.ReactElement => {
        const { label, color } = statusPresentation(
          params.row.status,
          translate,
        )
        return (
          <PillCell
            params={params}
            color={color}
            // The prototype's current Pill has an 8px horizontal inset; the
            // live DS channel still ships 4px. Scope the compatibility
            // treatment to this table instead of changing the shared Pill.
            style={{ paddingLeft: spacing(2), paddingRight: spacing(2) }}
            data-status={params.row.status}
          >
            {label}
          </PillCell>
        )
      },
    },
    {
      // Spread the shared drill-in chevron column so this row-end affordance is
      // metrically identical to the one on Transactions and Virtual Cards
      // (both build their trailing column from this): width 56 = 16 + 24 + 16,
      // no resize/sort/hide, no header label, no right separator.
      ...chevronColumnDef,
      // Only the cell body differs from the shared `ChevronCell`, which
      // renders a bare SVG that `onRowClick` cannot expose to the keyboard —
      // MUI does not fire row click from Enter/Space. Wrapping the same glyph
      // in a chrome-less ext-ui Button keeps the look identical and adds a
      // genuine keyboard path; the glyph is aria-hidden, so the accessible
      // name lives on the button and names the vendor. The click also bubbles
      // into `onRowClick`, hence the stopPropagation.
      renderCell: (params: GridCellParams<Vendor>): React.ReactElement => {
        const vendor = params.row
        // With the Bill Pay gate off, only Action Required rows have a caret —
        // the pre-EX-40771 column exactly. Rendering one on an inert row would
        // offer a keyboard path to nothing.
        if (!isVendorRowActionable(vendor)) {
          return (
            <DataGridCellWrapper params={params}>{null}</DataGridCellWrapper>
          )
        }
        // Per-status accessible name — Action Required keeps its exact label so
        // its existing test/keyboard path is unchanged; Active edits, Archived
        // views (read-only).
        const label =
          vendor.status === VendorStatus.ActionRequired
            ? translate('Finish setting up ${name}', { name: vendor.name })
            : vendor.status === VendorStatus.Archived
              ? translate('View ${name}', { name: vendor.name })
              : vendorEditEnabled
                ? translate('Edit ${name}', { name: vendor.name })
                : translate('View ${name}', { name: vendor.name })
        return (
          <DataGridCellWrapper params={params}>
            <Button
              variant={'no-style'}
              aria-label={label}
              onClick={(event): void => {
                event.stopPropagation()
                setFlyoutTarget(flyoutTargetForVendor(vendor))
              }}
              data-testid={
                vendor.status === VendorStatus.ActionRequired
                  ? 'vendor-resume-button'
                  : 'vendor-edit-button'
              }
            >
              <SvgChevronRight />
            </Button>
          </DataGridCellWrapper>
        )
      },
    },
  ]

  return (
    <LayoutPage fixedHeight>
      <Helmet>
        <title>{`${translate('Vendors')} | ${appName}`}</title>
      </Helmet>
      <div className={'VendorsPage'} css={style} data-testid={'vendors-page'}>
        <div className={'vendors-header'}>
          <h1 className={'vendors-title'}>{translate('Vendors')}</h1>
          {/* Primary CTA lives in the header, always visible — the empty
              state below is deliberately informational-only (prototype:
              "the empty state table shouldn't have ctas"). */}
          <Button
            variant={'primary'}
            iconLeft={<PlusIcon />}
            onClick={() => setFlyoutTarget({ mode: 'add' })}
          >
            {translate('Add vendor')}
          </Button>
        </div>
        {/* Prototype wraps both the empty state and the populated table in
            the same rounded/bordered container (app/vendors/page.tsx) — one
            Box, not a border only on the populated branch. */}
        <Box noPadding noShadow outlineBorder className={'vendors-box'}>
          {vendorsQuery.isError ? (
            <div className={'vendors-error'}>
              <AlertPanel variant={'severe'}>
                {translate('We could not load your vendors. Please try again.')}
              </AlertPanel>
            </div>
          ) : showPageEmptyState ? (
            <div
              className={'vendors-empty'}
              data-testid={'vendors-empty-state'}
            >
              <VendorsEmptyIllustration />
              <div className={'vendors-empty-text'}>
                <h2 className={'vendors-empty-title'}>
                  {translate('No vendors yet')}
                </h2>
                <p className={'vendors-empty-description'}>
                  {translate(
                    'Add a vendor with their name, email, and payment details to start paying them.',
                  )}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className={'vendors-toolbar'}>
                <span className={'vendors-count'}>
                  {pagination.totalItems === 1
                    ? translate('${count} vendor', {
                        count: pagination.totalItems,
                      })
                    : translate('${count} vendors', {
                        count: pagination.totalItems,
                      })}
                </span>
                <TextInput
                  className={'vendors-search'}
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value)
                    setPage(0)
                  }}
                  aria-label={translate('Search vendor names')}
                  placeholder={translate('Search vendor names')}
                />
              </div>
              {/* Measured viewport for the grid — see `.vendors-table`. */}
              <div className={'vendors-table'} ref={setTableViewport}>
                <DataGrid
                  height={tableHeight}
                  headerStyle={DataGridHeaderStyle.NoBackground}
                  rowHeight={DataGridRowHeight.Default}
                  rows={vendors}
                  columns={columns}
                  filtersBody={{}}
                  disableColumnFilter
                  rowSelection={false}
                  // Paging is server-side and rendered by `vendors-footer`
                  // below (the prototype's own treatment). Without this, MUI
                  // also renders its default footer and the card ends up with
                  // two stacked footer rows.
                  hideFooter
                  // With either edit (EX-40771) or archive (EX-40761) enabled,
                  // every row is actionable — Action Required resumes, all
                  // others open the same vendor flyout — so each row-end
                  // chevron is its keyboard path. With both off this is the
                  // pre-EX-40771 table: only Action Required rows open anything.
                  onRowClick={(params) => {
                    const target = flyoutTargetForVendor(params.row)
                    if (target) setFlyoutTarget(target)
                  }}
                  // `DataGrid` shows a pointer cursor on every row once
                  // `onRowClick` is passed at all — override it back to default
                  // for rows that open nothing.
                  getRowClassName={(params) =>
                    isVendorRowActionable(params.row) ? '' : 'vendor-row-inert'
                  }
                  sx={{
                    '& .MuiDataGrid-row.vendor-row-inert:hover': {
                      cursor: 'default',
                    },
                  }}
                />
              </div>
              {/* Pagination footer — the prototype's own treatment (Rows per
                  page + range + chevron buttons), not MUI's built-in pager,
                  which looks different and can't be styled to match exactly.
                  Right-aligned row with a top border, inside the shared Box. */}
              <div className={'vendors-footer'}>
                <span className={'vendors-footer-rows-per-page'}>
                  {translate('Rows per page: ${count}', { count: PAGE_SIZE })}
                </span>
                <span className={'vendors-footer-range'}>
                  {pagination.totalItems === 0
                    ? translate('0 of 0')
                    : translate('${start}-${end} of ${total}', {
                        start: rangeStart,
                        end: rangeEnd,
                        total: pagination.totalItems,
                      })}
                </span>
                <button
                  type={'button'}
                  className={'vendors-footer-button'}
                  aria-label={translate('Previous page')}
                  disabled={isFirstPage || vendorsQuery.isPlaceholderData}
                  onClick={() =>
                    setPage((previousPage) => Math.max(0, previousPage - 1))
                  }
                >
                  <ChevronIcon direction={'left'} />
                </button>
                <button
                  type={'button'}
                  className={'vendors-footer-button'}
                  aria-label={translate('Next page')}
                  disabled={isLastPage || vendorsQuery.isPlaceholderData}
                  onClick={() => setPage((previousPage) => previousPage + 1)}
                >
                  <ChevronIcon direction={'right'} />
                </button>
              </div>
            </>
          )}
        </Box>
        <VendorFlyout
          isOpen={flyoutTarget !== null}
          onClose={() => setFlyoutTarget(null)}
          organizationId={organizationId}
          resumeVendorId={
            flyoutTarget?.mode === 'resume' ? flyoutTarget.vendorId : undefined
          }
          editVendorId={
            flyoutTarget?.mode === 'edit' ? flyoutTarget.vendorId : undefined
          }
          editEnabled={vendorEditEnabled}
          archiveEnabled={vendorArchiveEnabled}
        />
      </div>
    </LayoutPage>
  )
}

export default VendorsPage
